import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useBasePath } from '../../../lib/use-base-path';
import { routeLink } from '../../../lib/route-link';
import { Card, Typography } from '../ui';
import styles from './AgentEntryRow.module.css';

// The compact entry-point row shared by the Your-Agents mobile summary tiles,
// Figma 15311:197080 → cards 197370 / 197388 / 197412. One line: the title (Heading 04), an optional
// inline subtitle that takes the slack, then the tile's trailing content and a 16px drill-in chevron.
//
// This mirrors native's `entryRowCard` (iOS `AgentsScreen`) / `AgentEntryRow` (Android `Pages.kt`) and
// is MOBILE-WEB ONLY: the desktop tiles keep their own two-row summary (heading-over-chips beside a
// centred chevron), so this is a viewport fork rather than a redesign — see this package's CLAUDE.md.
export function AgentEntryRow({
  title,
  subtitle,
  trailing,
  href,
  onClick,
  testId,
}: {
  title: string;
  subtitle?: string;
  trailing?: ReactNode;
  /** The bare in-app route; the mount prefix is added here. */
  href: string;
  onClick?: () => void;
  testId?: string;
}) {
  const basePath = useBasePath();
  return (
    <Card
      component={Link}
      interactive
      size="sm"
      className={styles.row}
      {...routeLink(basePath, href)}
      onClick={onClick}
      data-testid={testId}
    >
      <Typography variant="h4" color="strong" component="span" className={styles.title}>
        {title}
      </Typography>
      {subtitle != null ? (
        <Typography variant="p2" color="secondary" component="span" className={styles.subtitle}>
          {subtitle}
        </Typography>
      ) : (
        <span className={styles.spacer} />
      )}
      {trailing}
      <ChevronRight size={16} className={`${styles.chevron} dir-flip`} aria-hidden />
    </Card>
  );
}
